import React, { useState, useEffect } from 'react';
import { FaTimes, FaExclamationTriangle, FaMoneyBillWave } from 'react-icons/fa';
import { formatCurrency, formatDateForInput } from '../utils/formatters';
import './CancelBookingModal.css';

interface CancelBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCancel: (data: CancellationData) => Promise<void>;
  booking: {
    id: number;
    bookingId: string;
    customerName: string;
    unit: string;
    project: string;
    totalReceived: number;
  };
  loading?: boolean;
}

interface CancellationData {
  reason: string;
  refundAmount: number;
  refundDetails?: {
    refundDate: string;
    paymentMethod: string;
    referenceNumber: string;
    remarks?: string;
  };
}

const CancelBookingModal: React.FC<CancelBookingModalProps> = ({
  isOpen,
  onClose,
  onCancel,
  booking,
  loading = false
}) => {
  const [cancellationData, setCancellationData] = useState<CancellationData>({
    reason: '',
    refundAmount: 0
  });
  
  const [showRefundDetails, setShowRefundDetails] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setCancellationData({
        reason: '',
        refundAmount: 0
      });
      setShowRefundDetails(false);
      setErrors({});
    }
  }, [isOpen]);

  // Show refund details form when refund amount > 0
  useEffect(() => {
    const shouldShowRefund = cancellationData.refundAmount > 0;
    setShowRefundDetails(shouldShowRefund);
    
    if (shouldShowRefund && !cancellationData.refundDetails) {
      setCancellationData(prev => ({
        ...prev,
        refundDetails: {
          refundDate: formatDateForInput(new Date()),
          paymentMethod: 'cheque',
          referenceNumber: ''
        }
      }));
    }
  }, [cancellationData.refundAmount]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!cancellationData.reason.trim()) {
      newErrors.reason = 'Cancellation reason is required';
    }

    if (cancellationData.refundAmount < 0) {
      newErrors.refundAmount = 'Refund amount cannot be negative';
    }

    if (cancellationData.refundAmount > booking.totalReceived) {
      newErrors.refundAmount = `Refund amount cannot exceed received amount (₹${booking.totalReceived.toLocaleString('en-IN')})`;
    }

    if (showRefundDetails && cancellationData.refundDetails) {
      if (!cancellationData.refundDetails.referenceNumber.trim()) {
        newErrors.referenceNumber = 'Reference number is required for refunds';
      }
      if (!cancellationData.refundDetails.refundDate) {
        newErrors.refundDate = 'Refund date is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onCancel(cancellationData);
    } catch (error) {
      console.error('Cancellation failed:', error);
    }
  };

  if (!isOpen) return null;

  const retainedAmount = booking.totalReceived - cancellationData.refundAmount;

  return (
    <div className="modal-overlay">
      <div className="cancel-booking-modal">
        <div className="modal-header">
          <h2>
            <FaExclamationTriangle className="warning-icon" />
            Cancel Booking
          </h2>
          <button
            className="close-btn"
            onClick={onClose}
            disabled={loading}
          >
            <FaTimes />
          </button>
        </div>

        <div className="modal-content">
          {/* Booking Summary */}
          <div className="booking-summary">
            <h3>Booking Details</h3>
            <div className="summary-grid">
              <div className="summary-item">
                <span className="label">Booking ID:</span>
                <span className="value">{booking.bookingId}</span>
              </div>
              <div className="summary-item">
                <span className="label">Customer:</span>
                <span className="value">{booking.customerName}</span>
              </div>
              <div className="summary-item">
                <span className="label">Unit:</span>
                <span className="value">{booking.unit}</span>
              </div>
              <div className="summary-item">
                <span className="label">Project:</span>
                <span className="value">{booking.project}</span>
              </div>
              <div className="summary-item">
                <span className="label">Total Received:</span>
                <span className="value amount">{formatCurrency(booking.totalReceived)}</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Cancellation Reason */}
            <div className="form-group">
              <label>Cancellation Reason *</label>
              <textarea
                value={cancellationData.reason}
                onChange={(e) => setCancellationData(prev => ({
                  ...prev,
                  reason: e.target.value
                }))}
                placeholder="Please provide reason for cancellation..."
                rows={3}
                disabled={loading}
                className={errors.reason ? 'error' : ''}
              />
              {errors.reason && <span className="error-text">{errors.reason}</span>}
            </div>

            {/* Refund Amount */}
            <div className="form-group">
              <label>Refund Amount (Optional)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max={booking.totalReceived}
                value={cancellationData.refundAmount || ''}
                onChange={(e) => setCancellationData(prev => ({
                  ...prev,
                  refundAmount: parseFloat(e.target.value) || 0
                }))}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder="Enter refund amount (0 for no refund)"
                disabled={loading}
                className={errors.refundAmount ? 'error' : ''}
              />
              {errors.refundAmount && <span className="error-text">{errors.refundAmount}</span>}
              <small className="help-text">
                Maximum refundable: {formatCurrency(booking.totalReceived)}
              </small>
            </div>

            {/* Refund Summary */}
            {cancellationData.refundAmount > 0 && (
              <div className="refund-summary">
                <div className="summary-row">
                  <span>Amount Received:</span>
                  <span>{formatCurrency(booking.totalReceived)}</span>
                </div>
                <div className="summary-row">
                  <span>Refund Amount:</span>
                  <span className="refund">{formatCurrency(cancellationData.refundAmount)}</span>
                </div>
                <div className="summary-row total">
                  <span>Retained Amount:</span>
                  <span>{formatCurrency(retainedAmount)}</span>
                </div>
              </div>
            )}

            {/* Refund Details */}
            {showRefundDetails && (
              <div className="refund-details">
                <h4>
                  <FaMoneyBillWave />
                  Refund Payment Details
                </h4>
                
                <div className="form-grid">
                  <div className="form-group">
                    <label>Refund Date *</label>
                    <input
                      type="date"
                      value={cancellationData.refundDetails?.refundDate || ''}
                      onChange={(e) => setCancellationData(prev => ({
                        ...prev,
                        refundDetails: {
                          ...prev.refundDetails!,
                          refundDate: e.target.value
                        }
                      }))}
                      disabled={loading}
                      className={errors.refundDate ? 'error' : ''}
                    />
                    {errors.refundDate && <span className="error-text">{errors.refundDate}</span>}
                  </div>

                  <div className="form-group">
                    <label>Payment Method *</label>
                    <select
                      value={cancellationData.refundDetails?.paymentMethod || 'cheque'}
                      onChange={(e) => setCancellationData(prev => ({
                        ...prev,
                        refundDetails: {
                          ...prev.refundDetails!,
                          paymentMethod: e.target.value
                        }
                      }))}
                      disabled={loading}
                    >
                      <option value="cheque">Cheque</option>
                      <option value="neft">NEFT</option>
                      <option value="rtgs">RTGS</option>
                      <option value="imps">IMPS</option>
                      <option value="upi">UPI</option>
                      <option value="transfer">Transfer</option>
                    </select>
                  </div>

                  <div className="form-group full-width">
                    <label>Reference Number *</label>
                    <input
                      type="text"
                      value={cancellationData.refundDetails?.referenceNumber || ''}
                      onChange={(e) => setCancellationData(prev => ({
                        ...prev,
                        refundDetails: {
                          ...prev.refundDetails!,
                          referenceNumber: e.target.value
                        }
                      }))}
                      placeholder="Cheque number, transaction ID, etc."
                      disabled={loading}
                      className={errors.referenceNumber ? 'error' : ''}
                    />
                    {errors.referenceNumber && <span className="error-text">{errors.referenceNumber}</span>}
                  </div>

                  <div className="form-group full-width">
                    <label>Refund Remarks</label>
                    <textarea
                      value={cancellationData.refundDetails?.remarks || ''}
                      onChange={(e) => setCancellationData(prev => ({
                        ...prev,
                        refundDetails: {
                          ...prev.refundDetails!,
                          remarks: e.target.value
                        }
                      }))}
                      placeholder="Additional refund notes..."
                      rows={2}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="modal-actions">
              <button
                type="button"
                onClick={onClose}
                className="btn-cancel"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-confirm"
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Confirm Cancellation'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CancelBookingModal;